import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Plus, Trash2, Edit3, X } from 'lucide-react';
import { userPreferences } from '../../actions/actions';
import { putUserPreferences } from '../../services/leadService';
import BankDetailsForm from './forms/BankDetailsForm';
import FieldSelector from './forms/FieldSelector';
import ColorPickerField from './forms/ColorPickerField';
import Inputs from '../../components/inputs';
import { STATUS_COLOR_OPTION } from '../../utils/colors';

interface BankDetails {
  accountHolderName: string;
  accountNumber: string;
  bankName: string;
  ifscCode: string;
}

interface StatusItem {
  color: string;
  name: string;
  value: string;
  isDefault: boolean;
}

interface InvoiceQuotationData {
  bankDetails: BankDetails;
  leadFields: string[];
  organizationFields: string[];
  termsAndConditions: string;
  prefix?: string; // Only for quotation
  invoiceStatus?: StatusItem[]; // Only for invoice
  quotationStatus?: StatusItem[]; // Only for quotation
}

interface InvoiceQuotationCardProps {
  type: 'invoice' | 'quotation';
  title: string;
  color: string;
}

const PREDEFINED_ORG_FIELDS = [
  { name: 'Name', value: 'name' },
  { name: 'Phone', value: 'phone' },
  { name: 'Email', value: 'email' },
  { name: 'Address', value: 'address' },
  { name: 'Website', value: 'website' },
  { name: 'GST Number', value: 'gst_number' }
];

const InvoiceQuotationCard: React.FC<InvoiceQuotationCardProps> = ({ type, title, color }) => {
  const dispatch = useDispatch();
  const preferences = useSelector((state: any) => state?.userPreferencesData?.userPreferences || {});

  const customizationKey = type === 'invoice' ? 'invoiceCustomization' : 'quotationCustomization';
  const statusKey = type === 'invoice' ? 'invoiceStatus' : 'quotationStatus';

  const [isExpanded, setIsExpanded] = useState(false);
  const [formData, setFormData] = useState<InvoiceQuotationData>({
    bankDetails: {
      accountHolderName: '',
      accountNumber: '',
      bankName: '',
      ifscCode: ''
    },
    leadFields: [],
    organizationFields: [],
    termsAndConditions: '',
    prefix: type === 'quotation' ? 'Quote' : undefined,
    [statusKey]: []
  });

  const [statusFormData, setStatusFormData] = useState({
    name: '',
    color: STATUS_COLOR_OPTION[0].value,
    isDefault: false
  });
  const [editingStatusIndex, setEditingStatusIndex] = useState<number | null>(null);
  const [isAddingStatus, setIsAddingStatus] = useState(false);
  const [errors, setErrors] = useState<any>({});

  // Get available lead fields from customForm
  const availableLeadFields = preferences?.customForm?.map((field: any) => ({
    name: field.name,
    value: field.value
  })) || [];

  useEffect(() => {
    const existingData = preferences?.[customizationKey];
    if (existingData) {
      setFormData({
        bankDetails: existingData.bankDetails || formData.bankDetails,
        leadFields: existingData.leadFields || [],
        organizationFields: existingData.organizationFields || [],
        termsAndConditions: existingData.termsAndConditions || '',
        prefix: existingData.prefix || formData.prefix,
        [statusKey]: existingData[statusKey] || []
      });
    }
  }, [preferences, customizationKey]);

  const handleSave = async () => {
    const validationErrors: any = {};

    // Validate bank details
    if (!formData.bankDetails.accountHolderName?.trim()) {
      validationErrors['bankDetails.accountHolderName'] = 'Account holder name is required';
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      const updatedPreferences = {
        ...preferences,
        [customizationKey]: formData
      };

      await putUserPreferences(updatedPreferences);
      dispatch(userPreferences(updatedPreferences));
      setIsExpanded(false);
      setErrors({});
    } catch (error) {
      console.error('Failed to save customization:', error);
    }
  };

  const handleAddStatus = () => {
    if (!statusFormData.name.trim()) return;

    const newStatus: StatusItem = {
      name: statusFormData.name.trim(),
      value: statusFormData.name.trim().toLowerCase().replace(/\s+/g, '_'),
      color: statusFormData.color,
      isDefault: statusFormData.isDefault
    };

    const currentStatuses = (formData[statusKey] as StatusItem[]) || [];

    if (editingStatusIndex !== null) {
      const updatedStatuses = [...currentStatuses];
      updatedStatuses[editingStatusIndex] = newStatus;
      setFormData({ ...formData, [statusKey]: updatedStatuses });
      setEditingStatusIndex(null);
    } else {
      setFormData({ ...formData, [statusKey]: [...currentStatuses, newStatus] });
    }

    setStatusFormData({
      name: '',
      color: STATUS_COLOR_OPTION[0].value,
      isDefault: false
    });
    setIsAddingStatus(false);
  };

  const handleEditStatus = (index: number) => {
    const currentStatuses = (formData[statusKey] as StatusItem[]) || [];
    const status = currentStatuses[index];
    if (status) {
      setStatusFormData({
        name: status.name,
        color: status.color,
        isDefault: status.isDefault
      });
      setEditingStatusIndex(index);
      setIsAddingStatus(true);
    }
  };

  const handleDeleteStatus = (index: number) => {
    const currentStatuses = (formData[statusKey] as StatusItem[]) || [];
    setFormData({
      ...formData,
      [statusKey]: currentStatuses.filter((_, i) => i !== index)
    });
  };

  if (!isExpanded) {
    return (
      <div
        style={{
          backgroundColor: '#ffffff',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '12px',
          cursor: 'pointer',
          transition: 'all 0.2s'
        }}
        onClick={() => setIsExpanded(true)}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '32px',
                height: '32px',
                backgroundColor: color,
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            />
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
              {title}
            </span>
          </div>
          <Plus size={18} color="#6b7280" />
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: '#ffffff',
        border: '2px solid #3b82f6',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '12px'
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              width: '32px',
              height: '32px',
              backgroundColor: color,
              borderRadius: '6px'
            }}
          />
          <span style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
            {title}
          </span>
        </div>
        <button
          type="button"
          onClick={() => setIsExpanded(false)}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px'
          }}
        >
          <X size={20} color="#6b7280" />
        </button>
      </div>

      {/* Bank Details Section */}
      <div style={{ marginBottom: '20px' }}>
        <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
          Bank Details
        </h4>
        <BankDetailsForm
          value={formData.bankDetails}
          onChange={(bankDetails) => setFormData({ ...formData, bankDetails })}
          errors={{
            accountHolderName: errors['bankDetails.accountHolderName']
          }}
        />
      </div>

      {/* Status Section */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
            {type === 'invoice' ? 'Invoice Status' : 'Quotation Status'}
          </h4>
          {!isAddingStatus && (
            <button
              type="button"
              onClick={() => setIsAddingStatus(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                backgroundColor: color,
                color: '#ffffff',
                border: 'none',
                borderRadius: '4px',
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              <Plus size={14} />
              Add Status
            </button>
          )}
        </div>

        {isAddingStatus && (
          <div style={{
            backgroundColor: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            padding: '12px',
            marginBottom: '12px'
          }}>
            <Inputs
              label="Status Name"
              type="text"
              name="statusName"
              className="form-control"
              placeholder="Enter status name"
              autoComplete="off"
              isRequired={true}
              options={[]}
              id="statusName"
              value={statusFormData.name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setStatusFormData({ ...statusFormData, name: e.target.value })
              }
            />

            <ColorPickerField
              value={statusFormData.color}
              onChange={(c) => setStatusFormData({ ...statusFormData, color: c })}
              colorOptions={STATUS_COLOR_OPTION}
            />

            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '13px',
              color: '#374151',
              marginBottom: '12px',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={statusFormData.isDefault}
                onChange={(e) => setStatusFormData({ ...statusFormData, isDefault: e.target.checked })}
              />
              Set as default status
            </label>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                onClick={handleAddStatus}
                disabled={!statusFormData.name.trim()}
                style={{
                  backgroundColor: statusFormData.name.trim() ? color : '#d1d5db',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: statusFormData.name.trim() ? 'pointer' : 'not-allowed'
                }}
              >
                {editingStatusIndex !== null ? 'Update' : 'Add'} Status
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsAddingStatus(false);
                  setEditingStatusIndex(null);
                  setStatusFormData({
                    name: '',
                    color: STATUS_COLOR_OPTION[0].value,
                    isDefault: false
                  });
                }}
                style={{
                  backgroundColor: '#ffffff',
                  color: '#6b7280',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Status List */}
        {((formData[statusKey] as StatusItem[]) || []).map((status, index) => (
          <div
            key={index}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px 12px',
              backgroundColor: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: '4px',
              marginBottom: '6px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div
                style={{
                  width: '20px',
                  height: '20px',
                  backgroundColor: status.color,
                  borderRadius: '3px',
                  border: '1px solid #e5e7eb'
                }}
              />
              <span style={{ fontSize: '13px', color: '#374151' }}>{status.name}</span>
              {status.isDefault && (
                <span style={{
                  fontSize: '10px',
                  backgroundColor: '#dbeafe',
                  color: '#1e40af',
                  padding: '2px 6px',
                  borderRadius: '3px',
                  fontWeight: '500'
                }}>
                  DEFAULT
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                onClick={() => handleEditStatus(index)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <Edit3 size={14} color="#6b7280" />
              </button>
              <button
                type="button"
                onClick={() => handleDeleteStatus(index)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <Trash2 size={14} color="#ef4444" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Lead Fields Section */}
      <FieldSelector
        label="Lead Fields"
        availableFields={availableLeadFields}
        selectedValues={formData.leadFields}
        onChange={(values) => setFormData({ ...formData, leadFields: values })}
      />

      {/* Organization Fields Section */}
      <FieldSelector
        label="Organization Fields"
        availableFields={PREDEFINED_ORG_FIELDS}
        selectedValues={formData.organizationFields}
        onChange={(values) => setFormData({ ...formData, organizationFields: values })}
      />

      {/* Prefix (Quotation Only) */}
      {type === 'quotation' && (
        <div style={{ marginBottom: '16px' }}>
          <Inputs
            label="Quotation Prefix"
            type="text"
            name="prefix"
            className="form-control"
            placeholder="e.g., Quote, QT, etc."
            autoComplete="off"
            isRequired={false}
            options={[]}
            id="quotationPrefix"
            value={formData.prefix || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFormData({ ...formData, prefix: e.target.value })
            }
          />
        </div>
      )}

      {/* Terms & Conditions */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{
          display: 'block',
          marginBottom: '8px',
          fontSize: '13px',
          fontWeight: '500',
          color: '#374151'
        }}>
          Terms & Conditions
        </label>
        <textarea
          value={formData.termsAndConditions}
          onChange={(e) => setFormData({ ...formData, termsAndConditions: e.target.value })}
          placeholder="Enter your terms and conditions..."
          rows={4}
          style={{
            width: '100%',
            padding: '10px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            fontSize: '13px',
            fontFamily: 'inherit',
            resize: 'vertical',
            outline: 'none'
          }}
        />
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={() => {
            setIsExpanded(false);
            setErrors({});
          }}
          style={{
            backgroundColor: '#ffffff',
            color: '#6b7280',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            padding: '8px 16px',
            fontSize: '13px',
            fontWeight: '500',
            cursor: 'pointer'
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          style={{
            backgroundColor: color,
            color: '#ffffff',
            border: 'none',
            borderRadius: '6px',
            padding: '8px 16px',
            fontSize: '13px',
            fontWeight: '500',
            cursor: 'pointer'
          }}
        >
          Save Changes
        </button>
      </div>
    </div>
  );
};

export default InvoiceQuotationCard;
